import { useContext, useEffect, useState } from "react";
import { RightDrawerContext } from "../context/RightDrawerContext";
import {
  AddMembersContext,
  ApiErrorHandler,
  ChatInfoContext,
  IOrganizationContext,
  MembersToAddType,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import {
  DrawerDivider,
  DrawerHeader,
  EmptyBox,
} from "../layout/RightDrawer.style";
import AddMember from "../addMember/AddMember";
import { AddBroadcastGroupInfo } from "./AddBroadcastGroupInfo";
import {
  IBroadcastGroupData,
  IBroadcastInfo,
} from "@home-health-notify/domain";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { AddGroupWrapper } from "../common/Common.style";
import CommonDrawerHeader from "../common/CommonDrawerHeader";
import { ICreateBroadcastGroupRequest } from "@home-health-notify/models";

type EditBroadcastGroupProps = {
  chatId?: string;
};

export const AddBroadcastGroup = ({ chatId }: EditBroadcastGroupProps) => {
  const { t } = useTranslation();
  const doNothing = () => {};
  const tNsDrawer = (key: string) => t(key, { ns: "drawerHeader" });
  const tNsCommon = (key: string) => t(key, { ns: "common" });

  const { closeDrawer, openChatInfoDrawer } = useContext(RightDrawerContext);
  const { enqueueSnackbar } = useSnackbar();
  const { resetMembers } = useContext(AddMembersContext);

  const [showAddMembers, setShowAddMembers] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);

  const isEdit = !!chatId;

  const { chat } = useContext(ChatInfoContext);
  const broadcastInfo =
    isEdit && chat ? (chat?.chatInfo as IBroadcastInfo) : undefined;

  const [broadcastGroupData, setBroadcastGroupData] =
    useState<IBroadcastGroupData>(
      broadcastInfo
        ? {
            name: broadcastInfo.name,
            about: broadcastInfo.about,
          }
        : ({} as IBroadcastGroupData),
    );

  useEffect(() => {
    setBroadcastGroupData({
      name: broadcastInfo?.name || "",
      about: broadcastInfo?.about || "",
    });
  }, [broadcastInfo]);

  const onSetBroadcastGroupData = (data: IBroadcastGroupData) => {
    setBroadcastGroupData(data);
  };

  const getRequestData = (selectedMembers?: MembersToAddType) => {
    let broadcastGroupPayload: Partial<ICreateBroadcastGroupRequest> = {};
    if (
      selectedMembers?.membersToAdd &&
      selectedMembers.membersToAdd.length > 0
    )
      broadcastGroupPayload["membersToAdd"] = selectedMembers.membersToAdd.map(
        member => member.userId,
      );
    broadcastGroupPayload["about"] = broadcastGroupData.about;
    broadcastGroupPayload["name"] = broadcastGroupData.name;
    return broadcastGroupPayload;
  };

  const addBroadcastGroupInfo = (broadcastGroupData: IBroadcastGroupData) => {
    setBroadcastGroupData(broadcastGroupData);
    if (isEdit) {
      addBroadcastGroup(undefined, broadcastGroupData).then(() => {
        doNothing();
      });
    } else {
      setShowAddMembers(true);
    }
  };

  const resetForm = () => {
    setBroadcastGroupData({} as IBroadcastGroupData);
    setShowAddMembers(false);
  };

  const getEditRequestData = (id: string, data?: IBroadcastGroupData) => {
    const editPayload: { id: string; name?: string; about?: string } = { id };

    // Only include name if it has a value
    if (data?.name?.trim()) {
      editPayload.name = data.name;
    }

    // Only include about if it has a value
    if (data?.about?.trim()) {
      editPayload.about = data.about;
    }

    return editPayload;
  };

  const addBroadcastGroup = async (
    selectedMembers?: MembersToAddType,
    broadcastGroupData?: IBroadcastGroupData,
  ) => {
    try {
      setIsLoading(true);
      let broadcastGroupPayload;
      if (isEdit) {
        // Without an id the broadcast endpoint upserts into a new broadcast
        // rather than updating this one, so refuse to submit instead.
        const editId = broadcastInfo?.id;
        if (!editId) return;
        broadcastGroupPayload = getEditRequestData(editId, broadcastGroupData);
      } else {
        broadcastGroupPayload = getRequestData(selectedMembers);
      }

      await selectedOrgUser!.chatManager
        .getBroadcastChatManager()
        .addChat(broadcastGroupPayload);

      if (isEdit) {
        enqueueSnackbar(
          (broadcastGroupPayload["name"] || broadcastInfo?.name) +
            " " +
            tNsDrawer("editBroadcastSuccessMessage"),
          {
            autoHideDuration: 3000,
          },
        );
      } else {
        enqueueSnackbar(
          broadcastGroupPayload["name"] +
            " " +
            tNsDrawer("createBroadcastSuccessMessage"),
          {
            autoHideDuration: 3000,
          },
        );
      }
      if (!isEdit) {
        closeDrawer();
      } else {
        openChatInfo();
      }
      resetForm();
      resetMembers();
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const openChatInfo = () => {
    openChatInfoDrawer();
    resetForm();
  };

  return (
    <AddGroupWrapper>
      {isEdit ? (
        <DrawerHeader>
          <IconButton data-testid="back-button" onClick={() => openChatInfo()}>
            <ChevronLeftIcon fontSize={"large"} />
          </IconButton>
          <Typography variant="h6">{tNsCommon("editBroadcast")}</Typography>
          <EmptyBox />
        </DrawerHeader>
      ) : (
        <CommonDrawerHeader
          showBackButton={showAddMembers}
          onBackButtonClick={() => setShowAddMembers(false)}
          title={
            showAddMembers ? tNsDrawer("addMembers") : tNsDrawer("newBroadcast")
          }
          onCloseButtonClick={() => {
            closeDrawer();
            setShowAddMembers(false);
            setBroadcastGroupData({} as IBroadcastGroupData);
            resetMembers();
          }}
        />
      )}

      <Divider />
      {showAddMembers ? (
        <AddMember
          callback={addBroadcastGroup}
          isLoading={isLoading}
          hideGroupsTab={true}
          minMembers={2}
        />
      ) : (
        <Box
          sx={{
            flexDirection: "column",
            overflow: "auto"
          }}>
          <DrawerDivider />
          <AddBroadcastGroupInfo
            addBroadcastGroupInfo={addBroadcastGroupInfo}
            onSetBroadcastGroupData={onSetBroadcastGroupData}
            broadcastGroupData={broadcastGroupData}
            broadcastInfo={broadcastInfo}
          />
        </Box>
      )}
    </AddGroupWrapper>
  );
};
